<template>
  <div
    ref="el"
    :style="{
      width: '100%',
      height: typeof height === 'number' ? `${height}px` : height
    }"
  />
</template>

<script setup>
import {
  onMounted,
  onBeforeUnmount,
  ref,
  shallowRef,
  watch,
  computed,
  defineProps,
  defineEmits
} from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  rows: { type: Array, default: () => [] },

  height: { type: [Number, String], default: 460 },
  visibleCount: { type: Number, default: 7 },

  colorRange: { type: Array, default: () => ['#EDF5F1', '#0EE378', '#000D1C'] },

  wrapLabel: { type: Function, default: null },
  labelMaxWidth: { type: Number, default: 240 }
})

const emit = defineEmits(['bar-click'])

const el = ref(null)
const chart = shallowRef(null)
let ro = null

const hasZoom = computed(() => (props.rows?.length || 0) > props.visibleCount)

function calculateAxisParams(maxValue) {
  if (!maxValue || maxValue <= 0) return { max: 10, interval: 1 }

  const niceSteps = [
    1, 2, 5, 10, 20, 50, 100, 200, 500,
    1000, 2000, 5000, 10000, 20000, 50000,
    100000, 200000, 500000, 1000000, 2000000,
    5000000, 10000000
  ]

  let bestStep = 1
  for (let i = 0; i < niceSteps.length; i++) {
    if (niceSteps[i] * 10 >= maxValue) {
      bestStep = niceSteps[i]
      break
    }
  }

  const newMax = Math.ceil(maxValue / (bestStep * 10)) * (bestStep * 10)
  return { max: newMax, interval: newMax / 10 }
}

function buildOption() {
  const src = Array.isArray(props.rows) ? props.rows : []
  const maxInscr = src.reduce((m, r) => Math.max(m, Number(r?.inscricoes || 0)), 0)
  const { max, interval } = calculateAxisParams(maxInscr)

  const labelAreaPx = 220

  const endValue = Math.min(props.visibleCount - 1, Math.max(src.length - 1, 0))

  const sliderRight = 6
  const sliderWidth = hasZoom.value ? 10 : 0
  const gapBetween = hasZoom.value ? 10 : 8

  const visualMapReserve = 56

  const digits = String(Math.max(0, maxInscr)).length
  const valueLabelReserve = Math.min(110, 24 + digits * 8)

  const gridRight =
    sliderRight +
    sliderWidth +
    gapBetween +
    visualMapReserve +
    12 +
    valueLabelReserve

  const visualMapRight = sliderRight + sliderWidth + gapBetween

  return {
    animation: true,

    grid: {
      left: labelAreaPx,
      right: gridRight,
      top: 20,
      bottom: 20,
      containLabel: false
    },

    dataset: {
      dimensions: ['idCidade', 'cidade', 'inscricoes', 'probabilidade'],
      source: src
    },

    tooltip: {
      trigger: 'item',
      axisPointer: { type: 'shadow' },
      confine: true,
      backgroundColor: '#fff',
      borderColor: '#C5C7C6',
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: '#191C1C', fontSize: 12 },
      formatter: (p) => {
        const d = p?.data || {}
        const cidade = d.cidade || 'Sem cidade'
        const qtd = Number(d.inscricoes ?? 0)
        const prob = Number(d.probabilidade ?? 0)

        return `
          <strong>${cidade}</strong><br/>
          ${p.marker} Inscrições: ${qtd.toLocaleString('pt-BR')}<br/>
          Probabilidade: ${prob.toFixed(2)}%
        `
      }
    },

    xAxis: {
      type: 'value',
      min: 0,
      max,
      interval,
      axisLabel: { formatter: (v) => Number(v).toLocaleString('pt-BR') },
      splitLine: { show: true }
    },

    yAxis: {
      type: 'category',
      inverse: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        interval: 0,
        width: Math.max(80, labelAreaPx - 24),
        overflow: 'break',
        lineHeight: 14,
        formatter: (v) => (props.wrapLabel ? props.wrapLabel(v) : String(v))
      }
    },

    visualMap: {
      type: 'continuous',
      min: 0,
      max: 100,
      orient: 'vertical',
      right: visualMapRight,
      top: 110,
      itemHeight: 180,
      itemWidth: 12,
      calculable: false,
      inRange: { color: props.colorRange },
      formatter: (v) => `${v}%`,
      text: ['100%', '0%'],
      dimension: 'probabilidade'
    },

    dataZoom: hasZoom.value ? [
    {
      type: 'inside',
      yAxisIndex: 0,
      zoomOnMouseWheel: false,
      moveOnMouseWheel: true,
      moveOnMouseMove: true,
      preventDefaultMouseMove: true,
      startValue: 0,
      endValue,
      filterMode: 'empty'
    },
    {
      type: 'slider',
      yAxisIndex: 0,
      orient: 'vertical',
      right: 0,
      top: 16,
      bottom: 16,
      width: 10,
      showDetail: false,
      showDataShadow: false,
      brushSelect: false,
      zoomLock: true,
      borderRadius: 100,
      borderWidth: 0,
      borderColor: 'transparent',
      backgroundColor: {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#f5f5f5' },
          { offset: 1, color: '#e1e1e1' }
        ]
      },

      startValue: 0,
      endValue,
      filterMode: 'empty',
      fillerColor: '#8E9191',
      handleSize: '100%',
      handleIcon: 'path://M512 512m-512 0a512 512 0 1 0 1024 0 512 512 0 1 0-1024 0Z',
      handleStyle: {
        color: '#8E9191',
        borderWidth: 0,
        opacity: 1
      },

      moveHandleSize: 0,
      moveHandleStyle: { color: 'transparent', borderWidth: 0 }
    }
  ] : [],


    series: [
      {
        name: 'Inscrições',
        type: 'bar',
        barMaxWidth: 16,
        barCategoryGap: '35%',
        encode: {
          x: 'inscricoes',
          y: 'cidade',
          tooltip: ['cidade', 'inscricoes', 'probabilidade']
        },
        label: {
          show: true,
          position: 'right',
          distance: 6,
          formatter: (p) => Number(p?.data?.inscricoes ?? 0).toLocaleString('pt-BR'),
          fontSize: 11
        },
        emphasis: { focus: 'none' }
      }
    ]
  }
}


function render() {
  if (!chart.value) return
  chart.value.setOption(buildOption(), { notMerge: true, lazyUpdate: true })
}

onMounted(() => {
  if (!el.value) return
  chart.value = echarts.init(el.value)

  chart.value.on('click', (params) => {
    const d = params?.data
    if (d) emit('bar-click', d)
  })

  ro = new ResizeObserver(() => chart.value?.resize())
  ro.observe(el.value)

  render()
})

onBeforeUnmount(() => {
  if (ro && el.value) ro.unobserve(el.value)
  ro = null

  if (chart.value) {
    chart.value.dispose()
    chart.value = null
  }
})

watch(
  () => [props.rows, props.visibleCount, props.colorRange, props.labelMaxWidth],
  () => render(),
  { deep: true }
)
</script>
